import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";

interface FeatureFlags {
  enablePricing: boolean;
  // Hides the "Do you have a promo code?" entry on the quote summary.
  // Off by default — flip to true (or pass `?enablePromoCodes=true`) to
  // re-enable when there's an active promo to surface.
  enablePromoCodes: boolean;
  // Contracts v2 (DocuSign replacement) — gates the admin compose / list /
  // detail surfaces. Defaults OFF and rolls out per-coordinator for QA.
  // See docs/features/contracts/IMPLEMENTATION_PLAN.md.
  contracts_v2_enabled: boolean;
  // Separate flag gating the **Send** action on the v2 envelope composer.
  // Stays OFF through Phase 1 — the composer can save drafts but cannot
  // dispatch invites until Phase 2's signer flow + ESIGN consent screen
  // are live, so a Send action never lands on a 404 portal route.
  contracts_v2_send_enabled: boolean;
  // Add more feature flags here as needed
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isEnabled: (flag: keyof FeatureFlags) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

const DEFAULT_FLAGS: FeatureFlags = {
  enablePricing: true,
  enablePromoCodes: false,
  contracts_v2_enabled: false,
  contracts_v2_send_enabled: false,
};

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);

  useEffect(() => {
    // Parse query params on mount and whenever URL changes
    const parseQueryParams = () => {
      const params = new URLSearchParams(window.location.search);
      const newFlags: FeatureFlags = { ...DEFAULT_FLAGS };

      // Check each feature flag in query params
      Object.keys(DEFAULT_FLAGS).forEach((flagKey) => {
        const paramValue = params.get(flagKey);
        if (paramValue !== null) {
          // Convert string to boolean: 'true' or '1' = true, anything else = false
          newFlags[flagKey as keyof FeatureFlags] =
            paramValue === "true" || paramValue === "1";

          // Store in sessionStorage to persist across navigation
          sessionStorage.setItem(`featureFlag_${flagKey}`, paramValue);
        } else {
          // Check sessionStorage for previously set flags
          const stored = sessionStorage.getItem(`featureFlag_${flagKey}`);
          if (stored !== null) {
            newFlags[flagKey as keyof FeatureFlags] =
              stored === "true" || stored === "1";
          }
        }
      });

      setFlags(newFlags);
    };

    parseQueryParams();

    // Listen for URL changes (for SPA navigation)
    window.addEventListener("popstate", parseQueryParams);

    return () => {
      window.removeEventListener("popstate", parseQueryParams);
    };
  }, []);

  const isEnabled = (flag: keyof FeatureFlags): boolean => {
    return flags[flag];
  };

  const value = useMemo(() => ({ flags, isEnabled }), [flags]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return context;
};
